import { useState, useEffect, useRef } from 'react';
import '../styles/Testimonials.css';
import { useTestimonialsForHome } from '../context/HomeConfigContext.jsx';

function Testimonials() {
  const testimonials = useTestimonialsForHome();
  const [activeTestimonial, setActiveTestimonial] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollRef = useRef(null);

  const openModal = (item) => setActiveTestimonial(item);
  const closeModal = () => setActiveTestimonial(null);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const threshold = 4;
    const hasMoreLeft = scrollLeft > threshold;
    const hasMoreRight = scrollLeft + clientWidth < scrollWidth - threshold;
    setCanScrollLeft(hasMoreLeft);
    setCanScrollRight(hasMoreRight);
  };

  const scrollTestimonials = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector('.tm-testimonial-card');
    const gap = 24;
    const step = (card?.offsetWidth || 400) + gap;
    el.scrollBy({ left: direction === 'left' ? -step : step, behavior: 'smooth' });
    setTimeout(checkScroll, 350);
  };

  useEffect(() => {
    const el = scrollRef.current;
    const runCheck = () => {
      checkScroll();
    };
    runCheck();
    const t = setTimeout(runCheck, 100);
    if (el) {
      el.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }
    return () => {
      clearTimeout(t);
      if (el) el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    if (activeTestimonial) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [activeTestimonial]);

  if (testimonials.length === 0) return null;

  return (
    <section id="testimonios" className="tm-section tm-testimonials-section">
      <div className="tm-container">
        <div className="tm-testimonials-header">
          <h3 className="tm-testimonials-subtitle">Historias Reales</h3>
          <h2 className="tm-testimonials-title">
            Testimonios de
            <span className="tm-testimonials-title-italic">nuestras modelos</span>
          </h2>
        </div>

        <div className="tm-testimonials-scroll-wrap">
          <div ref={scrollRef} className="tm-testimonials-scroll no-scrollbar">
            {testimonials.map((item) => (
              <button
                key={item.id}
                type="button"
                className="tm-testimonial-card"
                onClick={() => openModal(item)}
                aria-label={`Leer experiencia de ${item.name}`}
              >
                <div
                  className="tm-testimonial-card-bg"
                  style={{ backgroundImage: `url(${item.image})` }}
                  aria-hidden="true"
                />
                <div className="tm-testimonial-card-burbuja">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="tm-testimonial-card-burbuja-img"
                    loading="lazy"
                  />
                </div>
                <div className="tm-testimonial-card-overlay" aria-hidden="true" />
                <div className="tm-testimonial-card-badge">{item.name.toUpperCase()}</div>
                <p className="tm-testimonial-card-preview">{item.preview}</p>
                <span className="tm-testimonial-card-cta">
                  Ver experiencia
                  <span className="material-symbols-outlined tm-testimonial-card-cta-icon">arrow_forward</span>
                </span>
              </button>
            ))}
          </div>
          <div className="tm-testimonials-nav">
            <button
              type="button"
              className="tm-testimonials-nav-btn"
              onClick={() => scrollTestimonials('left')}
              disabled={!canScrollLeft}
              aria-label="Anterior"
            >
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <button
              type="button"
              className="tm-testimonials-nav-btn"
              onClick={() => scrollTestimonials('right')}
              disabled={!canScrollRight}
              aria-label="Siguiente"
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      {activeTestimonial && (
        <div
          className="tm-testimonial-modal-overlay"
          onClick={closeModal}
          role="presentation"
        >
          <div
            className="tm-testimonial-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="testimonial-modal-title"
            aria-describedby="testimonial-modal-content"
          >
            <button
              type="button"
              className="tm-testimonial-modal-close"
              onClick={closeModal}
              aria-label="Cerrar"
            >
              <span className="material-symbols-outlined">close</span>
            </button>

            <div className="tm-testimonial-modal-content">
              <div className="tm-testimonial-modal-header">
                <div className="tm-testimonial-modal-avatar-wrap">
                  <img
                    src={activeTestimonial.image}
                    alt={activeTestimonial.name}
                    className="tm-testimonial-modal-avatar"
                  />
                </div>
                <div className="tm-testimonial-modal-meta">
                  <span className="tm-testimonial-modal-tag">{activeTestimonial.tag}</span>
                  <h2 id="testimonial-modal-title" className="tm-testimonial-modal-name">
                    {activeTestimonial.name}
                  </h2>
                </div>
              </div>
              <div id="testimonial-modal-content" className="tm-testimonial-modal-text">
                <span className="material-symbols-outlined tm-testimonial-modal-quote">format_quote</span>
                <p>{activeTestimonial.experience}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Testimonials;
